import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function StatCard({ label, value, sub, color = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-card p-4 shadow-sm">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="mt-0.5 text-xs font-medium text-slate-500">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

/* Pass/fail stacked bar trend — pure CSS, no chart dependency. */
function TrendChart({ trend }) {
  if (!trend || trend.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-slate-400">
        No runs in the selected window.
      </div>
    );
  }
  const max = Math.max(...trend.map((t) => t.total), 1);
  return (
    <div className="flex items-end gap-1.5 overflow-x-auto pb-2" style={{ height: 180 }}>
      {trend.map((t) => {
        const h = (t.total / max) * 150;
        const passH = t.total ? (t.passed / t.total) * h : 0;
        const failH = t.total ? (t.failed / t.total) * h : 0;
        return (
          <div key={t.date} className="flex min-w-[26px] flex-1 flex-col items-center gap-1">
            <div
              className="flex w-6 flex-col justify-end overflow-hidden rounded"
              style={{ height: h }}
              title={`${t.date}: ${t.passed} passed, ${t.failed} failed`}
            >
              <div className="w-full bg-rose-400" style={{ height: failH }} />
              <div className="w-full bg-emerald-500" style={{ height: passH }} />
            </div>
            <span className="rotate-0 text-[9px] text-slate-400">{t.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsView({ boards = [] }) {
  const [boardId, setBoardId] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (boardId) params.set("board_id", boardId);
      const res = await fetch(`${API_BASE_URL}reports/analytics?${params.toString()}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setData(body?.data || null);
    } catch (e) {
      setError(e.message || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, [boardId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const selectCls =
    "rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className={selectCls}>
          <option value="">All test suites</option>
          {boards.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={selectCls}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          <Icons.Refresh className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" />
          Loading analytics…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : !data ? null : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label="Pass rate" value={`${Math.round((data.pass_rate || 0) * 100)}%`} color="text-emerald-600" />
            <StatCard label="Total runs" value={data.total_runs} />
            <StatCard label="Failed runs" value={data.failed_runs} color="text-rose-600" />
            <StatCard label="Coverage" value={`${data.coverage_pct}%`} sub={`${data.testcases_executed}/${data.testcases_total} tests`} />
            <StatCard label="Avg duration" value={`${data.avg_duration_seconds}s`} />
            <StatCard label="Open defects" value={data.open_defects} color="text-rose-600" sub="latest run failed" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Pass / fail trend</h3>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm bg-emerald-500" /> Passed
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm bg-rose-400" /> Failed
                </span>
              </div>
            </div>
            <TrendChart trend={data.trend} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">
              Flaky tests <span className="text-sm font-normal text-slate-400">({data.flaky?.length || 0})</span>
            </h3>
            {(!data.flaky || data.flaky.length === 0) ? (
              <p className="py-6 text-center text-sm text-slate-400">
                No flaky tests detected (tests that both passed and failed across runs).
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      <th className="py-2 pr-4">Test case</th>
                      <th className="py-2 pr-4">Runs</th>
                      <th className="py-2 pr-4">Passed</th>
                      <th className="py-2 pr-4">Failed</th>
                      <th className="py-2 pr-4">Flip rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.flaky.map((f) => (
                      <tr key={f.testcasekey} className="border-t border-slate-100">
                        <td className="py-2 pr-4 text-slate-800">{f.name}</td>
                        <td className="py-2 pr-4 text-slate-600">{f.runs}</td>
                        <td className="py-2 pr-4 text-emerald-600">{f.passed}</td>
                        <td className="py-2 pr-4 text-rose-600">{f.failed}</td>
                        <td className="py-2 pr-4">
                          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-600/20">
                            {Math.round(f.flip_rate * 100)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
